import { useCallback, useMemo } from 'react';
import type { IModuleDefinition, IRecordTypeDefinition } from '@platform/core/types';
import { useRecordTypeEditor } from '@platform/features/workspaces/builder';
import { findModuleDefinition } from '@platform/modularity/moduleDefinitionRegistry';
import { useGlobalRecordTypeOverrideQuery } from '@platform/state/api';
import { withProtoForm } from './formPatch';
import { formModelOf } from './formSummary';
import type { IFormEditor } from './IFormEditor';
import type { IFormScope } from './IFormScope';
import type { IProtoForm } from './IProtoForm';
import { hasForms, scopeFormOf } from './scopeForm';
import { resetChanges, scopeResetPatch } from './scopeReset';

/**
 * Редактор форми типу записів (прототип v4 cust-form): у просторі — перевизначення простору (ADR-023), на рівні «Глобально» (`global`) —
 * глобальне перевизначення (ADR-027, для всіх просторів без власної форми). Модель — з патча свого рівня (зі зміною, що ще зберігається) і
 * злитого визначення типу цього рівня; простір без власних форм показує успадковану глобальну форму (глобальний патч читає будь-хто), і перша
 * зміна пише її всю в патч простору. Кожна зміна одразу йде PUT …/override з If-Match (черга useRecordTypeEditor); скидання — патч без форми
 * або DELETE, коли патч порожній.
 */
export function useFormEditor(ws: string, module: IModuleDefinition, rt: IRecordTypeDefinition, global = false): IFormEditor {
  const moduleKey = module.module.key;
  const editor = useRecordTypeEditor(ws, moduleKey, rt.key, global);
  const inherited = useGlobalRecordTypeOverrideQuery({ rt: rt.key }, { skip: global || !rt.key });
  const published = useMemo(() => findModuleDefinition(moduleKey)?.recordTypes.find((r) => r.key === rt.key), [moduleKey, rt.key]);
  const scope = useMemo<IFormScope>(() => ({ global, inherited: inherited.data?.patch }), [global, inherited.data]);
  const loaded = editor.loaded && (global || !!inherited.data || !!inherited.error);
  // Доки перевизначення вантажаться — модель за злитим визначенням (форма видна одразу, без стрибка зі стандартного макета).
  const fallback = useMemo(() => (loaded ? undefined : formModelOf(rt, published)), [loaded, rt, published]);
  const model = useMemo(() => scopeFormOf(rt, editor.patch, published, scope), [rt, editor.patch, published, scope]);
  const { apply: applyPatch, reset: resetAll, patch } = editor;
  const apply = useCallback((edit: (f: IProtoForm) => IProtoForm) => {
    applyPatch((p) => withProtoForm(p, rt, edit(scopeFormOf(rt, p, published, scope).form), published));
  }, [applyPatch, rt, published, scope]);
  const resettable = useMemo(() => resetChanges(patch, rt, published, scope), [patch, rt, published, scope]);
  const reset = useCallback(() => {
    const next = scopeResetPatch(patch, rt, published, scope);
    if (next) applyPatch(() => next);
    else resetAll();
  }, [applyPatch, resetAll, patch, rt, published, scope]);
  return {
    form: fallback?.form ?? model.form, source: fallback?.source ?? model.source, published, global,
    globalForm: hasForms(global ? patch : scope.inherited), canEdit: editor.canEdit && loaded, resettable, apply, reset,
  };
}
